/**
 * AuctionHouseDialog — Tabbed dialog for browsing, selling, managing, and
 * viewing transaction history on the mineral trade hub.
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { MMOStateManager } from '../../managers/MMOStateManager';
import type { AuctionListing, AuctionTransaction, PlayerMineral, MarketSummary } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, Fonts, FontSize, Radius, createDialogChrome, closeDialog } from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

const PAD = 14;

const MARKETPLACE_FEE_PCT = 2.5;

// Rarity color map — matches MineralInventoryDialog
const RARITY_COLORS: Record<string, string> = {
  common:    '#aaaaaa',
  uncommon:  '#4488ff',
  rare:      '#ffcc00',
  epic:      '#cc66ff',
  legendary: '#ff8800',
};

type TabName = 'browse' | 'sell' | 'my_listings' | 'history';
type SortKey = 'mineral' | 'qty' | 'price' | 'total';
type SortDir = 'asc' | 'desc';

export class AuctionHouseDialog extends Phaser.GameObjects.Container {
  private monetization: MMOMonetizationManager;
  private mmoState: MMOStateManager;
  private activeTab: TabName = 'browse';

  // Data
  private listings: AuctionListing[] = [];
  private myListings: AuctionListing[] = [];
  private transactions: AuctionTransaction[] = [];
  private inventory: PlayerMineral[] = [];
  private marketSummary: MarketSummary[] = [];

  // Sell form state
  private selectedMineralIndex = 0;
  private sellQuantity = 1;
  private sellPrice = 1;

  // Browse buy state
  private buyingListing: AuctionListing | null = null;
  private buyQuantity = 0;

  // Sort state
  private sortKey: SortKey = 'price';
  private sortDir: SortDir = 'asc';

  // UI containers
  private tabContainer: Phaser.GameObjects.Container;
  private contentContainer: Phaser.GameObjects.Container;
  private scrollMask!: Phaser.Display.Masks.GeometryMask;
  private balanceText: Phaser.GameObjects.Text;
  private statusText: Phaser.GameObjects.Text;
  private refreshBtn: Phaser.GameObjects.Container;

  /** Optional: pre-select a mineral on the Sell tab when opened from Ore Inventory */
  private initialMineralTypeId: string | null = null;

  /* Responsive sizing */
  private dialogW: number;
  private contentW: number;
  private scrollH: number;

  constructor(scene: Phaser.Scene, opts?: { tab?: TabName; mineralTypeId?: string }) {
    const mobile = isMobile(scene);
    const DIALOG_W = mobile ? 340 : 652;
    const DIALOG_H = mobile ? 540 : 580;
    const CONTENT_W = DIALOG_W - PAD * 2;
    const SCROLL_H = mobile ? 430 : 390;

    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - DIALOG_W / 2, height / 2 - DIALOG_H / 2);

    this.dialogW = DIALOG_W;
    this.contentW = CONTENT_W;
    this.scrollH = SCROLL_H;

    if (opts?.tab) this.activeTab = opts.tab;
    if (opts?.mineralTypeId) this.initialMineralTypeId = opts.mineralTypeId;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);
    this.mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);

    createDialogChrome(scene, this, DIALOG_W, DIALOG_H, {
      title: I18n.t('dialog.trade_hub'),
      onClose: () => this.close(),
    });

    // Balance display (top right, next to close button)
    const bal = this.mmoState.getResources();
    this.balanceText = scene.add.text(DIALOG_W - 50, 26, `$${bal.toLocaleString()}`, {
      fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading,
    });
    this.balanceText.setOrigin(1, 0.5);
    this.add(this.balanceText);

    const balLabel = scene.add.text(this.balanceText.x - this.balanceText.width - 4, 26, 'BAL:', {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
    });
    balLabel.setOrigin(1, 0.5);
    this.add(balLabel);

    // Tab bar
    this.tabContainer = scene.add.container(0, 55);
    this.add(this.tabContainer);
    this.renderTabs();

    // Scrollable content area with mask
    this.contentContainer = scene.add.container(PAD, 95);
    this.add(this.contentContainer);
    this.setupScrollMask();

    // Status text + refresh
    this.statusText = scene.add.text(DIALOG_W / 2, DIALOG_H - 15, '', {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.refreshBtn = this.createActionBtn(DIALOG_W - 42, DIALOG_H - 28, 30, 22, '↻', 0x1a2a44, () => this.loadTabData());
    this.add(this.refreshBtn);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadTabData();
  }

  private close(): void {
    closeDialog(this.scene, this);
  }

  /** Set up a geometry mask so content scrolls within bounds */
  private setupScrollMask(): void {
    const maskGraphics = this.scene.add.graphics();
    maskGraphics.fillStyle(0xffffff);
    // Position in world coords
    maskGraphics.fillRect(this.x + PAD, this.y + 95, this.contentW, this.scrollH);
    maskGraphics.setVisible(false);
    this.scrollMask = new Phaser.Display.Masks.GeometryMask(this.scene, maskGraphics);
    this.contentContainer.setMask(this.scrollMask);

    // Mouse-wheel scrolling
    this.scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      const contentH = this.contentContainer.getBounds().height;
      if (contentH <= this.scrollH) return;
      const maxScroll = -(contentH - this.scrollH);
      const localY = this.contentContainer.y - 95; // offset from base
      const newLocalY = Phaser.Math.Clamp(localY - dy * 0.5, maxScroll, 0);
      this.contentContainer.y = 95 + newLocalY;
    });
  }

  private updateBalance(): void {
    const bal = this.mmoState.getResources();
    this.balanceText.setText(`$${bal.toLocaleString()}`);
  }

  // ============ Tabs ============

  private renderTabs(): void {
    this.tabContainer.removeAll(true);

    const tabs: { key: TabName; label: string }[] = [
      { key: 'browse', label: 'BROWSE' },
      { key: 'sell', label: 'SELL' },
      { key: 'my_listings', label: 'MY LISTINGS' },
      { key: 'history', label: 'HISTORY' },
    ];

    const tabW = this.dialogW / tabs.length;

    tabs.forEach((tab, i) => {
      const isActive = this.activeTab === tab.key;
      const x = i * tabW;

      const tabBg = this.scene.add.graphics();
      const drawTabBg = (fill: number, borderColor: number, borderAlpha: number) => {
        tabBg.clear();
        tabBg.fillStyle(fill, 1);
        tabBg.fillRect(x, 0, tabW, 32);
        tabBg.lineStyle(1, borderColor, borderAlpha);
        tabBg.strokeRect(x, 0, tabW, 32);
        if (isActive) {
          tabBg.lineStyle(2, Colors.cyan, 0.5);
          tabBg.lineBetween(x, 31, x + tabW, 31);
        }
      };
      drawTabBg(isActive ? 0x253a66 : 0x0e1525, isActive ? Colors.cyan : Colors.borderDim, isActive ? 0.3 : 0.15);
      this.tabContainer.add(tabBg);

      const tabText = this.scene.add.text(x + tabW / 2, 16, tab.label, {
        fontSize: FontSize.sm,
        color: isActive ? CSSColors.textWhite : '#667788',
        fontFamily: Fonts.heading,
      });
      tabText.setOrigin(0.5);
      this.tabContainer.add(tabText);

      const hitZone = this.scene.add.zone(x, 0, tabW, 32).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, tabW, 32), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => {
        if (!isActive) { drawTabBg(0x1a2a44, Colors.cyan, 0.2); tabText.setColor('#aabbcc'); }
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        drawTabBg(isActive ? 0x253a66 : 0x0e1525, isActive ? Colors.cyan : Colors.borderDim, isActive ? 0.3 : 0.15);
        tabText.setColor(isActive ? CSSColors.textWhite : '#667788');
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', () => {
        this.activeTab = tab.key;
        this.buyingListing = null;
        this.renderTabs();
        this.loadTabData();
      });
      this.tabContainer.add(hitZone);
    });
  }

  private async loadTabData(): Promise<void> {
    this.statusText.setText('Loading...');
    this.contentContainer.y = 95; // reset scroll
    this.updateBalance();

    switch (this.activeTab) {
      case 'browse':
        // Fetch enough to cover all active listings across all mineral types.
        // With the previous limit of 50 sorted by price_asc, higher-tier minerals (rhodium,
        // palladium, iridium, dark matter) got buried behind cheaper iron/copper/titanium.
        this.listings = await this.monetization.fetchAuctionListings({ limit: 500, sort: 'price_asc' });
        this.statusText.setText(`${this.listings.length} listing(s)`);
        if (this.buyingListing) {
          this.renderBuyConfirm();
        } else {
          this.renderBrowse();
        }
        break;
      case 'sell':
        [this.inventory, this.marketSummary] = await Promise.all([
          this.monetization.fetchMineralInventory(),
          this.monetization.fetchMarketSummary(),
        ]);
        // If opened with a pre-selected mineral, find its index
        if (this.initialMineralTypeId) {
          const idx = this.inventory.findIndex(m => m.mineralTypeId === this.initialMineralTypeId);
          if (idx >= 0) this.selectedMineralIndex = idx;
          this.initialMineralTypeId = null; // only use once
        }
        this.statusText.setText(`${this.inventory.length} mineral type(s) in inventory`);
        this.renderSell();
        break;
      case 'my_listings':
        this.myListings = await this.monetization.fetchMyAuctionListings();
        this.statusText.setText(`${this.myListings.length} active listing(s)`);
        this.renderMyListings();
        break;
      case 'history':
        this.transactions = await this.monetization.fetchAuctionTransactions();
        this.statusText.setText(`${this.transactions.length} transaction(s)`);
        this.renderHistory();
        break;
    }
  }

  // ============ Browse Tab ============

  private getSortedListings(): AuctionListing[] {
    const sorted = [...this.listings];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (this.sortKey) {
        case 'mineral': cmp = a.mineralName.localeCompare(b.mineralName); break;
        case 'qty': cmp = a.remainingQuantity - b.remainingQuantity; break;
        case 'price': cmp = a.pricePerUnit - b.pricePerUnit; break;
        case 'total': cmp = (a.remainingQuantity * a.pricePerUnit) - (b.remainingQuantity * b.pricePerUnit); break;
      }
      return this.sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }

  private renderBrowse(): void {
    this.contentContainer.removeAll(true);
    let y = 0;

    // Column layout
    const cols = { mineral: 0, qty: 200, price: 300, total: 420, action: this.contentW - 72 };

    // Sortable headers
    const headers: { x: number; label: string; key: SortKey }[] = [
      { x: cols.mineral, label: 'MINERAL', key: 'mineral' },
      { x: cols.qty, label: 'QTY', key: 'qty' },
      { x: cols.price, label: 'PRICE/UNIT', key: 'price' },
      { x: cols.total, label: 'TOTAL', key: 'total' },
    ];

    for (const h of headers) {
      const isActive = this.sortKey === h.key;
      const arrow = isActive ? (this.sortDir === 'asc' ? ' ▲' : ' ▼') : '';
      const text = this.scene.add.text(h.x + 10, y, `${h.label}${arrow}`, {
        fontSize: FontSize.xs,
        color: isActive ? CSSColors.textCyan : '#667788',
        fontFamily: Fonts.heading,
      });
      text.setInteractive();
      text.on('pointerover', () => { text.setColor(CSSColors.cyan); this.scene.input.setDefaultCursor('pointer'); });
      text.on('pointerout', () => { text.setColor(isActive ? CSSColors.textCyan : '#667788'); this.scene.input.setDefaultCursor('default'); });
      text.on('pointerdown', () => {
        if (this.sortKey === h.key) {
          this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortKey = h.key;
          this.sortDir = 'asc';
        }
        this.renderBrowse();
      });
      this.contentContainer.add(text);
    }
    y += 22;
    this.addDivider(y); y += 6;

    if (this.listings.length === 0) {
      const empty = this.scene.add.text(this.contentW / 2, y + 40, 'No listings available.', {
        fontSize: FontSize.md, color: '#556677', fontFamily: Fonts.body,
      });
      empty.setOrigin(0.5, 0);
      this.contentContainer.add(empty);
      return;
    }

    const sorted = this.getSortedListings();
    for (const listing of sorted) {
      const row = this.createRow(0, y, this.contentW, 38);

      // Rarity-colored mineral name
      const rarityColor = RARITY_COLORS[listing.rarity || 'common'] || RARITY_COLORS.common;

      // Small rarity dot
      const dot = this.scene.add.graphics();
      dot.fillStyle(parseInt(rarityColor.replace('#', ''), 16), 1);
      dot.fillCircle(16, 19, 4);
      row.add(dot);

      const name = this.scene.add.text(26, 10, listing.mineralName, {
        fontSize: FontSize.sm, color: rarityColor, fontFamily: Fonts.body,
      });
      row.add(name);

      row.add(this.scene.add.text(cols.qty + 10, 10, String(listing.remainingQuantity), {
        fontSize: FontSize.sm, color: '#aaccdd', fontFamily: Fonts.body,
      }));

      row.add(this.scene.add.text(cols.price + 10, 10, `$${listing.pricePerUnit.toFixed(2)}`, {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.body,
      }));

      const totalPrice = listing.remainingQuantity * listing.pricePerUnit;
      row.add(this.scene.add.text(cols.total + 10, 10, `$${totalPrice.toFixed(2)}`, {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.body,
      }));

      // Expiry indicator
      const expiryText = this.formatExpiry(listing.expiresAt);
      if (expiryText) {
        const expiry = this.scene.add.text(cols.action + 72, 24, expiryText.label, {
          fontSize: '10px', color: expiryText.color, fontFamily: Fonts.body,
        });
        expiry.setOrigin(1, 0);
        row.add(expiry);
      }

      // Buy button
      row.add(this.createActionBtn(cols.action, 5, 68, 28, 'Buy', 0x1a4422, () => {
        this.buyingListing = listing;
        this.buyQuantity = 1;
        this.renderBuyConfirm();
      }));

      this.contentContainer.add(row);
      y += 42;
    }
  }

  // ============ Buy Confirmation ============

  private renderBuyConfirm(): void {
    this.contentContainer.removeAll(true);
    const listing = this.buyingListing;
    if (!listing) { this.renderBrowse(); return; }

    let y = 0;

    // Back button
    this.contentContainer.add(this.createActionBtn(0, y, 60, 24, '← Back', 0x1a2a44, () => {
      this.buyingListing = null;
      this.renderBrowse();
    }));
    y += 34;

    // Title
    const rarityColor = RARITY_COLORS[listing.rarity || 'common'] || RARITY_COLORS.common;
    const title = this.scene.add.text(0, y, `Buy ${listing.mineralName}`, {
      fontSize: FontSize.lg, color: rarityColor, fontFamily: Fonts.heading,
    });
    this.contentContainer.add(title);
    y += 28;

    this.addInfoRow(0, y, 'Price per unit', `$${listing.pricePerUnit.toFixed(2)}`, CSSColors.gold);
    y += 22;
    this.addInfoRow(0, y, 'Available', String(listing.remainingQuantity), '#aaccdd');
    y += 28;

    // Quantity controls
    const qtyLabel = this.scene.add.text(0, y + 3, 'Quantity:', {
      fontSize: FontSize.sm, color: '#aaccff', fontFamily: Fonts.body,
    });
    this.contentContainer.add(qtyLabel);

    this.contentContainer.add(this.createActionBtn(100, y, 28, 26, '−', 0x442222, () => {
      if (this.buyQuantity > 1) { this.buyQuantity--; this.renderBuyConfirm(); }
    }));

    const qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.min = '1';
    qtyInput.max = String(listing.remainingQuantity);
    qtyInput.step = '1';
    qtyInput.value = String(this.buyQuantity);
    qtyInput.style.cssText = [
      'width:56px',
      'height:24px',
      'background:#0a1428',
      'border:1px solid #2a4060',
      'border-radius:4px',
      'outline:none',
      'color:#ffffff',
      'font-family:Orbitron,sans-serif',
      'font-size:14px',
      'font-weight:bold',
      'text-align:center',
      'padding:0',
      'margin:0',
      '-moz-appearance:textfield',
    ].join(';');
    qtyInput.addEventListener('input', () => {
      const raw = parseInt(qtyInput.value, 10);
      if (!isNaN(raw) && raw >= 1 && raw <= listing.remainingQuantity) {
        this.buyQuantity = raw;
      }
    });
    qtyInput.addEventListener('blur', () => {
      const raw = parseInt(qtyInput.value, 10);
      if (isNaN(raw) || raw < 1) this.buyQuantity = 1;
      else if (raw > listing.remainingQuantity) this.buyQuantity = listing.remainingQuantity;
      else this.buyQuantity = raw;
      this.renderBuyConfirm();
    });
    const qtyDom = this.scene.add.dom(160, y + 13, qtyInput).setOrigin(0.5, 0.5);
    this.contentContainer.add(qtyDom);

    this.contentContainer.add(this.createActionBtn(192, y, 28, 26, '+', 0x224422, () => {
      if (this.buyQuantity < listing.remainingQuantity) { this.buyQuantity++; this.renderBuyConfirm(); }
    }));

    y += 32;

    const presets: Array<[number, string]> = [
      [0.25, '25%'],
      [0.50, '50%'],
      [0.75, '75%'],
      [1.00, 'Max'],
    ];
    const presetW = 56;
    const presetGap = 8;
    let presetX = 0;
    for (const [frac, label] of presets) {
      this.contentContainer.add(this.createActionBtn(presetX, y, presetW, 26, label, 0x222244, () => {
        const target = Math.max(1, Math.floor(listing.remainingQuantity * frac));
        this.buyQuantity = Math.min(target, listing.remainingQuantity);
        this.renderBuyConfirm();
      }));
      presetX += presetW + presetGap;
    }
    y += 36;

    this.addDivider(y); y += 10;

    // Total cost
    const totalCost = this.buyQuantity * listing.pricePerUnit;
    this.addInfoRow(0, y, 'Total Cost', `$${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, CSSColors.gold);
    y += 22;

    const bal = this.mmoState.getResources();
    const canAfford = bal >= totalCost;
    this.addInfoRow(0, y, 'Your Balance', `$${bal.toLocaleString()}`, canAfford ? CSSColors.green : CSSColors.red);
    y += 28;

    if (!canAfford) {
      const warn = this.scene.add.text(0, y, 'Insufficient funds', {
        fontSize: FontSize.sm, color: CSSColors.red, fontFamily: Fonts.heading,
      });
      this.contentContainer.add(warn);
      y += 24;
    }

    // Confirm button
    const btnColor = canAfford ? 0x1a4422 : 0x332222;
    this.contentContainer.add(this.createActionBtn(0, y, 200, 36, `Confirm Purchase — $${totalCost.toFixed(2)}`, btnColor, () => {
      if (canAfford) this.executeBuy(listing, this.buyQuantity);
    }));
  }

  // ============ Sell Tab ============

  private renderSell(): void {
    this.contentContainer.removeAll(true);
    let y = 0;

    if (this.inventory.length === 0) {
      const empty = this.scene.add.text(this.contentW / 2, y + 40, 'No minerals to sell.\nMine asteroids first!', {
        fontSize: FontSize.md, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      empty.setOrigin(0.5, 0);
      this.contentContainer.add(empty);
      return;
    }

    // Build summary lookup
    const summaryMap = new Map<string, MarketSummary>();
    for (const s of this.marketSummary) summaryMap.set(s.mineralTypeId, s);

    // Section: Select mineral
    this.addSectionHeader(0, y, 'SELECT MINERAL');
    y += 20;

    const maxVisible = Math.min(this.inventory.length, 6);
    for (let i = 0; i < maxVisible; i++) {
      const mineral = this.inventory[i];
      const isSelected = i === this.selectedMineralIndex;
      const rarityColor = RARITY_COLORS[mineral.rarity] || RARITY_COLORS.common;

      const card = this.scene.add.container(0, y);
      const cardBg = this.scene.add.graphics();
      const drawCard = (selected: boolean, hover: boolean) => {
        cardBg.clear();
        const fill = selected ? 0x253a66 : hover ? 0x1e2e55 : Colors.bgCard;
        cardBg.fillStyle(fill, 0.9);
        cardBg.fillRoundedRect(0, 0, this.contentW, 32, Radius.sm);
        const bColor = selected ? Colors.cyan : hover ? Colors.cyan : Colors.borderDim;
        const bAlpha = selected ? 0.3 : hover ? 0.15 : 0.08;
        cardBg.lineStyle(1, bColor, bAlpha);
        cardBg.strokeRoundedRect(0, 0, this.contentW, 32, Radius.sm);
      };
      drawCard(isSelected, false);
      card.add(cardBg);

      // Rarity dot
      const dot = this.scene.add.graphics();
      dot.fillStyle(parseInt(rarityColor.replace('#', ''), 16), 1);
      dot.fillCircle(14, 16, 4);
      card.add(dot);

      const nameText = this.scene.add.text(24, 8, mineral.displayName || mineral.mineralName, {
        fontSize: FontSize.sm,
        color: isSelected ? CSSColors.textWhite : rarityColor,
        fontFamily: isSelected ? Fonts.heading : Fonts.body,
      });
      card.add(nameText);

      // Market price hint
      const summary = summaryMap.get(mineral.mineralTypeId);
      if (summary && summary.recentAvgPrice > 0) {
        const avgText = this.scene.add.text(this.contentW - 80, 8, `avg $${summary.recentAvgPrice.toFixed(2)}`, {
          fontSize: '10px', color: '#556677', fontFamily: Fonts.body,
        });
        avgText.setOrigin(1, 0);
        card.add(avgText);
      }

      const amountText = this.scene.add.text(this.contentW - 10, 8, `x${mineral.amount}`, {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.body,
      });
      amountText.setOrigin(1, 0);
      card.add(amountText);

      const hitZone = this.scene.add.zone(0, 0, this.contentW, 32).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.contentW, 32), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => { if (!isSelected) drawCard(false, true); this.scene.input.setDefaultCursor('pointer'); });
      hitZone.on('pointerout', () => { drawCard(isSelected, false); this.scene.input.setDefaultCursor('default'); });
      hitZone.on('pointerdown', () => {
        this.selectedMineralIndex = i;
        this.sellQuantity = 1;
        // Auto-fill price from market avg
        const s = summaryMap.get(mineral.mineralTypeId);
        if (s && s.recentAvgPrice > 0) this.sellPrice = Math.round(s.recentAvgPrice);
        this.renderSell();
      });
      card.add(hitZone);

      this.contentContainer.add(card);
      y += 36;
    }

    y += 8;
    this.addDivider(y); y += 10;

    const selectedMineral = this.inventory[this.selectedMineralIndex];
    if (!selectedMineral) return;

    // Market context
    const selSummary = summaryMap.get(selectedMineral.mineralTypeId);
    if (selSummary) {
      const contextRow = this.scene.add.container(0, y);
      const low = selSummary.lowestPrice > 0 ? `$${selSummary.lowestPrice.toFixed(2)}` : '—';
      const avg = selSummary.recentAvgPrice > 0 ? `$${selSummary.recentAvgPrice.toFixed(2)}` : '—';
      const high = selSummary.highestPrice > 0 ? `$${selSummary.highestPrice.toFixed(2)}` : '—';

      contextRow.add(this.scene.add.text(0, 0, `Market: Low ${low}  Avg ${avg}  High ${high}`, {
        fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
      }));
      this.contentContainer.add(contextRow);
      y += 18;
    }

    // Quantity row
    const qtyLabel = this.scene.add.text(0, y + 4, 'Quantity:', {
      fontSize: FontSize.sm, color: '#aaccff', fontFamily: Fonts.body,
    });
    this.contentContainer.add(qtyLabel);

    this.contentContainer.add(this.createActionBtn(110, y, 32, 26, '−', 0x442222, () => {
      if (this.sellQuantity > 1) { this.sellQuantity--; this.renderSell(); }
    }));

    const qtyVal = this.scene.add.text(160, y + 4, String(this.sellQuantity), {
      fontSize: FontSize.md, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    qtyVal.setOrigin(0.5, 0);
    this.contentContainer.add(qtyVal);

    this.contentContainer.add(this.createActionBtn(178, y, 32, 26, '+', 0x224422, () => {
      if (this.sellQuantity < selectedMineral.amount) { this.sellQuantity++; this.renderSell(); }
    }));

    this.contentContainer.add(this.createActionBtn(220, y, 44, 26, 'Max', 0x222244, () => {
      this.sellQuantity = selectedMineral.amount;
      this.renderSell();
    }));
    y += 34;

    // Price per unit row
    const priceLabel = this.scene.add.text(0, y + 4, 'Price/Unit:', {
      fontSize: FontSize.sm, color: '#aaccff', fontFamily: Fonts.body,
    });
    this.contentContainer.add(priceLabel);

    this.contentContainer.add(this.createActionBtn(110, y, 32, 26, '−', 0x442222, () => {
      if (this.sellPrice > 1) { this.sellPrice--; this.renderSell(); }
    }));

    const priceVal = this.scene.add.text(160, y + 4, `$${this.sellPrice}`, {
      fontSize: FontSize.md, color: CSSColors.gold, fontFamily: Fonts.heading,
    });
    priceVal.setOrigin(0.5, 0);
    this.contentContainer.add(priceVal);

    this.contentContainer.add(this.createActionBtn(178, y, 32, 26, '+', 0x224422, () => {
      this.sellPrice++; this.renderSell();
    }));

    this.contentContainer.add(this.createActionBtn(220, y, 44, 26, '+10', 0x222244, () => {
      this.sellPrice += 10; this.renderSell();
    }));
    y += 38;

    // Summary
    const totalPrice = this.sellQuantity * this.sellPrice;
    const fee = totalPrice * (MARKETPLACE_FEE_PCT / 100);
    const netProceeds = totalPrice - fee;

    this.addInfoRow(0, y, 'Gross Total', `$${totalPrice.toFixed(2)}`, '#aaccdd');
    y += 20;
    this.addInfoRow(0, y, `Fee (${MARKETPLACE_FEE_PCT}%)`, `-$${fee.toFixed(2)}`, CSSColors.red);
    y += 20;
    this.addInfoRow(0, y, 'You Receive', `$${netProceeds.toFixed(2)}`, CSSColors.gold);
    y += 28;

    this.contentContainer.add(
      this.createActionBtn(0, y, 180, 34, 'Create Listing', 0x1a4422, () => this.handleCreateListing()),
    );
  }

  // ============ My Listings Tab ============

  private renderMyListings(): void {
    this.contentContainer.removeAll(true);
    let y = 0;

    const cols = { mineral: 0, remaining: 180, price: 300, status: 410, action: this.contentW - 72 };

    this.addHeaderText(cols.mineral, y, 'MINERAL');
    this.addHeaderText(cols.remaining, y, 'REMAINING');
    this.addHeaderText(cols.price, y, 'PRICE/UNIT');
    this.addHeaderText(cols.status, y, 'STATUS');
    y += 22;
    this.addDivider(y); y += 6;

    if (this.myListings.length === 0) {
      const empty = this.scene.add.text(this.contentW / 2, y + 40, 'No active listings.', {
        fontSize: FontSize.md, color: '#556677', fontFamily: Fonts.body,
      });
      empty.setOrigin(0.5, 0);
      this.contentContainer.add(empty);
      return;
    }

    for (const listing of this.myListings) {
      const row = this.createRow(0, y, this.contentW, 42);
      const rarityColor = RARITY_COLORS[listing.rarity || 'common'] || RARITY_COLORS.common;

      // Rarity dot + name
      const dot = this.scene.add.graphics();
      dot.fillStyle(parseInt(rarityColor.replace('#', ''), 16), 1);
      dot.fillCircle(16, 14, 4);
      row.add(dot);

      row.add(this.scene.add.text(26, 6, listing.mineralName, {
        fontSize: FontSize.sm, color: rarityColor, fontFamily: Fonts.body,
      }));

      row.add(this.scene.add.text(cols.remaining + 10, 6, `${listing.remainingQuantity}/${listing.quantity}`, {
        fontSize: FontSize.sm, color: '#aaccdd', fontFamily: Fonts.body,
      }));

      row.add(this.scene.add.text(cols.price + 10, 6, `$${listing.pricePerUnit.toFixed(2)}`, {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.body,
      }));

      const statusColor = listing.status === 'active' ? CSSColors.green : '#667788';
      row.add(this.scene.add.text(cols.status + 10, 6, listing.status.toUpperCase(), {
        fontSize: FontSize.xs, color: statusColor, fontFamily: Fonts.heading,
      }));

      // Expiry
      const expiryText = this.formatExpiry(listing.expiresAt);
      if (expiryText && listing.status === 'active') {
        row.add(this.scene.add.text(cols.status + 10, 22, expiryText.label, {
          fontSize: '10px', color: expiryText.color, fontFamily: Fonts.body,
        }));
      }

      if (listing.status === 'active') {
        row.add(this.createActionBtn(cols.action, 7, 68, 28, 'Cancel', 0x442222, () => this.handleCancel(listing.id)));
      }

      this.contentContainer.add(row);
      y += 46;
    }
  }

  // ============ History Tab ============

  private renderHistory(): void {
    this.contentContainer.removeAll(true);
    let y = 0;

    const cols = { mineral: 0, qty: 150, price: 220, total: 320, role: 430, date: 520 };

    this.addHeaderText(cols.mineral, y, 'MINERAL');
    this.addHeaderText(cols.qty, y, 'QTY');
    this.addHeaderText(cols.price, y, 'PRICE');
    this.addHeaderText(cols.total, y, 'TOTAL');
    this.addHeaderText(cols.role, y, 'ROLE');
    this.addHeaderText(cols.date, y, 'DATE');
    y += 22;
    this.addDivider(y); y += 6;

    if (this.transactions.length === 0) {
      const empty = this.scene.add.text(this.contentW / 2, y + 40, 'No transaction history.', {
        fontSize: FontSize.md, color: '#556677', fontFamily: Fonts.body,
      });
      empty.setOrigin(0.5, 0);
      this.contentContainer.add(empty);
      return;
    }

    const currentUserId = this.mmoState.getUserId();
    const maxVisible = Math.min(this.transactions.length, 20);

    for (let i = 0; i < maxVisible; i++) {
      const tx = this.transactions[i];
      const row = this.createRow(0, y, this.contentW, 34);

      row.add(this.scene.add.text(10, 8, tx.mineralName, {
        fontSize: FontSize.xs, color: CSSColors.textWhite, fontFamily: Fonts.body,
      }));

      row.add(this.scene.add.text(cols.qty + 10, 8, String(tx.quantity), {
        fontSize: FontSize.xs, color: '#aaccdd', fontFamily: Fonts.body,
      }));

      row.add(this.scene.add.text(cols.price + 10, 8, `$${tx.pricePerUnit.toFixed(2)}`, {
        fontSize: FontSize.xs, color: CSSColors.gold, fontFamily: Fonts.body,
      }));

      row.add(this.scene.add.text(cols.total + 10, 8, `$${tx.totalPrice.toFixed(2)}`, {
        fontSize: FontSize.xs, color: CSSColors.gold, fontFamily: Fonts.body,
      }));

      // Buyer/seller based on actual user ID
      const isBuyer = tx.buyerId === currentUserId;
      const roleLabel = isBuyer ? 'BOUGHT' : 'SOLD';
      const roleColor = isBuyer ? '#44aaff' : CSSColors.green;
      row.add(this.scene.add.text(cols.role + 10, 8, roleLabel, {
        fontSize: FontSize.xs, color: roleColor, fontFamily: Fonts.heading,
      }));

      const dateStr = new Date(tx.completedAt).toLocaleDateString();
      row.add(this.scene.add.text(cols.date + 10, 8, dateStr, {
        fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
      }));

      this.contentContainer.add(row);
      y += 38;
    }
  }

  // ============ Actions ============

  private async executeBuy(listing: AuctionListing, quantity: number): Promise<void> {
    this.statusText.setText('Purchasing...');
    const result = await this.monetization.buyAuctionListing(listing.id, quantity);
    if (result.success) {
      this.statusText.setText(`Purchased ${quantity}x ${listing.mineralName}!`);
      this.buyingListing = null;
      this.updateBalance();
      this.loadTabData();
    } else {
      this.statusText.setText(result.error || 'Purchase failed');
    }
  }

  private async handleCreateListing(): Promise<void> {
    const selectedMineral = this.inventory[this.selectedMineralIndex];
    if (!selectedMineral) return;

    this.statusText.setText('Creating listing...');
    const result = await this.monetization.createAuctionListing(
      selectedMineral.mineralTypeId,
      this.sellQuantity,
      this.sellPrice,
    );
    if (result.success) {
      this.statusText.setText('Listing created!');
      this.sellQuantity = 1;
      this.sellPrice = 1;
      this.loadTabData();
    } else {
      this.statusText.setText(result.error || 'Create listing failed');
    }
  }

  private async handleCancel(listingId: string): Promise<void> {
    this.statusText.setText('Cancelling...');
    const result = await this.monetization.cancelAuctionListing(listingId);
    if (result.success) {
      this.statusText.setText('Listing cancelled');
      this.loadTabData();
    } else {
      this.statusText.setText(result.error || 'Cancel failed');
    }
  }

  // ============ Helpers ============

  private addHeaderText(x: number, y: number, text: string): void {
    const t = this.scene.add.text(x + 10, y, text, {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.heading,
    });
    this.contentContainer.add(t);
  }

  private addDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.08);
    g.lineBetween(0, y, this.contentW, y);
    this.contentContainer.add(g);
  }

  private addInfoRow(x: number, y: number, label: string, value: string, valueColor: string): void {
    const lbl = this.scene.add.text(x, y, `${label}: `, {
      fontSize: FontSize.sm, color: '#889999', fontFamily: Fonts.body,
    });
    this.contentContainer.add(lbl);

    const val = this.scene.add.text(x + lbl.width, y, value, {
      fontSize: FontSize.sm, color: valueColor, fontFamily: Fonts.body,
    });
    this.contentContainer.add(val);
  }

  private addSectionHeader(x: number, y: number, text: string): void {
    const t = this.scene.add.text(x, y, text, {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    });
    this.contentContainer.add(t);
  }

  /** Format an expiry date as a countdown label */
  private formatExpiry(expiresAt: string): { label: string; color: string } | null {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const msLeft = expiry.getTime() - now.getTime();
    if (msLeft <= 0) return { label: 'EXPIRED', color: CSSColors.red };

    const hours = Math.floor(msLeft / (1000 * 60 * 60));
    const mins = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));

    let label: string;
    let color: string;
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      label = `${days}d ${hours % 24}h left`;
      color = '#556677';
    } else if (hours >= 1) {
      label = `${hours}h ${mins}m left`;
      color = hours < 6 ? '#ffaa44' : '#556677';
    } else {
      label = `${mins}m left`;
      color = CSSColors.red;
    }
    return { label, color };
  }

  private createRow(x: number, y: number, w: number, h: number): Phaser.GameObjects.Container {
    const row = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(Colors.bgCard, 0.7);
    bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    bg.lineStyle(1, Colors.borderDim, 0.08);
    bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    row.add(bg);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(Colors.bgCardHover, 0.8);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.12);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(Colors.bgCard, 0.7);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.borderDim, 0.08);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    });
    row.add(hitZone);

    return row;
  }

  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, color: number, callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.15);
    bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color + 0x111111, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.3);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.15);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', callback);
    btn.add(hitZone);

    return btn;
  }
}
